import { useCallback, useEffect, useId, useLayoutEffect, useMemo, useRef, useState } from "react";

import styles from "./select.module.css";
import { RequiredMark } from "./required-mark";

/**
 * The one dropdown, used everywhere a field has a known finite set of answers.
 *
 * ── Why not `<select>` ──────────────────────────────────────────────────────
 *
 * A native select is uneditable and remembers its value, which is most of what
 * was asked for — and it **cannot be searched**. Four hundred timezones, three
 * hundred currencies and two hundred countries are not lists anybody scrolls;
 * the browser's own type-ahead matches only a prefix of the label, so somebody
 * looking for Beirut by typing "beirut" lands nowhere because the label starts
 * with "(GMT+3)".
 *
 * So this is a combobox: a button that opens a filtered list. The three
 * properties the client asked for are structural rather than enforced —
 *
 *   · **The value cannot be typed into.** The trigger is a `<button>`; there is
 *     no text input holding the value, so "USD" cannot become "USDj". The only
 *     writable box is the filter, and it never becomes the value.
 *   · **Reopening shows the current choice**, highlighted and scrolled to,
 *     rather than the top of the list.
 *   · **Choosing again just replaces it**, because the value is one slot.
 *
 * ── Accessibility ───────────────────────────────────────────────────────────
 *
 * `role="combobox"` on the trigger with `aria-expanded` and `aria-controls`, a
 * `role="listbox"` of `role="option"`s, and `aria-activedescendant` following
 * the highlight so a screen reader announces the row the arrow keys are on.
 * Escape closes, Enter chooses, Home and End jump — a person who never touches
 * the mouse gets the same control as one who does.
 */
export type SelectOption = {
  value: string;
  /** What the row reads as, and what the filter matches against. */
  label: string;
  /** Extra matchable text — a currency's name, a country's dial code. */
  keywords?: string;
  /** Shown greyed at the end of the row. Never matched against. */
  hint?: string;
  /** Optional heading this option sits under. */
  group?: string;
};

export function Select({
  id,
  label,
  value,
  options,
  onChange,
  testId,
  note,
  placeholder = "Choose…",
  required,
  disabled,
  invalid,
  emptyLabel,
}: {
  id: string;
  label: string;
  value: string;
  options: SelectOption[];
  onChange: (next: string) => void;
  testId: string;
  note?: string;
  placeholder?: string;
  /** Draws the red star: the form cannot be saved without an answer. */
  required?: boolean;
  disabled?: boolean;
  /** Draws the error treatment. The message itself belongs to the form. */
  invalid?: boolean;
  /** When set, an explicit "no answer" row is offered with this wording. */
  emptyLabel?: string;
}): React.JSX.Element {
  const listId = useId();
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const [active, setActive] = useState(0);

  const rootRef = useRef<HTMLDivElement | null>(null);
  const triggerRef = useRef<HTMLButtonElement | null>(null);
  const searchRef = useRef<HTMLInputElement | null>(null);
  const listRef = useRef<HTMLUListElement | null>(null);

  /**
   * The highlighted row, readable from a callback ref.
   *
   * `attachList` runs at the moment the list is attached and has to know where
   * to scroll. Depending on `active` instead would re-create the ref callback
   * on every arrow key, remounting nothing but re-running the scroll and
   * fighting the keyboard navigation.
   */
  const activeRef = useRef(0);
  activeRef.current = active;

  /**
   * Where the popup sits, in viewport coordinates.
   *
   * The popup is `position: fixed` so an ancestor's `overflow` cannot clip it —
   * see `select.module.css`. The price is that it no longer follows the trigger
   * on its own, so this measures it.
   */
  /**
   * Where the popup is pinned.
   *
   * **Either a top or a bottom, never both** — see `place` for why an upward
   * popup has to be anchored by its own bottom edge rather than by a guess at
   * its height.
   */
  const [anchor, setAnchor] = useState<{
    top?: number;
    bottom?: number;
    left: number;
    width: number;
    /** The room on the side it opened towards — see `place`. */
    maxHeight: number;
  } | null>(null);

  const place = useCallback(() => {
    const trigger = triggerRef.current;
    if (trigger === null) return;

    const rect = trigger.getBoundingClientRect();

    /**
     * **Opens upward when there is not room below**, which is the common case
     * for the last field in a dialog. 320px is the popup's own ceiling —
     * a 17rem list plus its search box — so this asks whether the whole thing
     * fits rather than guessing.
     */
    const wanted = 320;
    const below = window.innerHeight - rect.bottom;
    const flip = below < wanted && rect.top > below;

    /**
     * **Never taller than the room on the side it opens towards** (2026-09-25).
     *
     * Opening upward only helps when there is room upward. In a short window —
     * a 1366x768 laptop at 200% zoom is 683x325 in CSS pixels — neither side
     * has 320px, and the list ran off the bottom of the screen with its last
     * rows unreachable, because the popup is fixed and scrolling the page does
     * not move it. The cap goes on the popup and the list inside it shrinks to
     * fit (see `.popup` in the stylesheet), so the search box always shows and
     * the rows scroll within whatever height is left — 4px off the trigger and
     * 8px clear of the window's edge, the same margins the anchors below use.
     */
    const maxHeight = Math.max(0, Math.floor((flip ? rect.top : below) - 4 - 8));

    if (flip) {
      /**
       * **Pinned by its bottom edge, not by `top: rect.top - 320`.**
       *
       * That was the bug the client reported on the patient editor's Insurer
       * field: *"the dropdown appears waaaay above where it should"*. 320 is the
       * popup's **maximum** height, and a field whose list holds two insurers
       * draws about 90px — so subtracting the maximum left a 230px hole between
       * the list and the field it belongs to, and the list floated near the top
       * of the dialog with nothing connecting the two.
       *
       * Anchoring the bottom instead lets the popup's real height decide where
       * its top lands, so it always sits directly above the trigger whatever it
       * contains — and it stays correct as typing filters the list shorter,
       * which the old form could not do at all without re-measuring after every
       * keystroke.
       */
      setAnchor({
        bottom: Math.max(8, window.innerHeight - rect.top + 4),
        left: rect.left,
        width: rect.width,
        maxHeight,
      });
      return;
    }

    setAnchor({ top: rect.bottom + 4, left: rect.left, width: rect.width, maxHeight });
  }, []);

  /**
   * The rows actually offered, with the "not set" row first when there is one.
   *
   * Built here rather than by every caller so the empty answer is a real option
   * — arrow-navigable, filterable, announced — instead of a special case each
   * screen implements slightly differently.
   */
  const all = useMemo<SelectOption[]>(
    () => (emptyLabel === undefined ? options : [{ value: "", label: emptyLabel }, ...options]),
    [options, emptyLabel],
  );

  const filtered = useMemo(() => {
    const needle = query.trim().toLowerCase();
    if (needle === "") return all;
    return all.filter((option) => {
      const haystack = `${option.label} ${option.keywords ?? ""} ${option.value}`.toLowerCase();
      return haystack.includes(needle);
    });
  }, [all, query]);

  const selected = all.find((option) => option.value === value);

  /**
   * Opening lands on the current value, not on row zero.
   *
   * `useLayoutEffect` so the scroll happens before the browser paints —
   * otherwise the list flashes at the top and jumps, which reads as a bug on a
   * list of four hundred.
   */
  useLayoutEffect(() => {
    if (!open) return;

    const at = filtered.findIndex((option) => option.value === value);
    const landing = at === -1 ? 0 : at;
    setActive(landing);

    // **The scroll and the focus are NOT done here** — see `attachSearch` and
    // `attachList` below. When this runs, `open` has only just become true and
    // the popup is still unmounted: it renders on `open && anchor !== null`,
    // and `anchor` is measured by a later effect. So both refs are null at
    // this point and both calls were silently doing nothing.
    //
    // Only `setActive` belongs here, because it is state the popup reads on
    // the render that mounts it.

    /**
     * **`open` alone, deliberately.**
     *
     * `filtered` and `value` are read here and are not dependencies: this runs
     * when the popup *opens*, to land on the current choice. Including them
     * would re-run it on every keystroke and fight the effect below, which
     * resets the highlight to the top as the list narrows — the cursor would
     * jump back to the selected row while somebody was still typing.
     */
  }, [open]);

  // Typing narrows the list, so whatever was highlighted is usually gone.
  useEffect(() => {
    if (open && query !== "") setActive(0);
  }, [query, open]);

  /**
   * Focus the filter box the moment it exists.
   *
   * **This is what makes "click a dropdown and start typing" work**, and it was
   * reported as missing across the whole application. The focus call used to
   * live in the layout effect that runs when `open` flips, one render before
   * the popup mounts — so it always ran against a null ref and the keystrokes
   * went to whatever had focus before, usually the dialog behind. A callback
   * ref cannot be early: React calls it with the node at the moment of
   * attachment.
   *
   * `useCallback` with no dependencies keeps the identity stable, so React
   * calls it on mount and unmount only. An inline arrow would be re-created
   * every render and re-run on every keystroke.
   */
  const attachSearch = useCallback((node: HTMLInputElement | null) => {
    searchRef.current = node;
    node?.focus();
  }, []);

  /**
   * Scroll the current value into view once the list exists — the other half
   * of the same bug. On a list of four hundred timezones, opening at row zero
   * instead of at the selected row is the difference between confirming a
   * choice and hunting for it.
   *
   * `activeRef` rather than `active` in the dependency list: this must run when
   * the list *mounts*, and reading the highlight through a ref keeps that from
   * turning into "re-run on every arrow key", which would fight the keyboard
   * navigation below.
   */
  const attachList = useCallback((node: HTMLUListElement | null) => {
    listRef.current = node;
    if (node === null) return;
    const row = node.children[activeRef.current];
    if (row instanceof HTMLElement) row.scrollIntoView({ block: "center" });
  }, []);

  const close = useCallback(() => {
    setOpen(false);
    setQuery("");
    /**
     * **Forget where the popup was**, so the next open measures before it
     * draws.
     *
     * Kept, the stale anchor made the popup render on the *first* commit after
     * `open` — before the layout effect above had moved the highlight onto the
     * current value. `attachList` therefore scrolled to the previous open's
     * row, and reopening a four-hundred-row list landed at the top instead of
     * on the selected timezone. It also meant the list flashed at last time's
     * coordinates for a frame whenever the field had moved.
     */
    setAnchor(null);
  }, []);

  // A click anywhere else closes, which is what everybody expects and what
  // makes the popup feel like a menu rather than a panel.
  useEffect(() => {
    if (!open) return;

    const onPointerDown = (event: MouseEvent): void => {
      if (rootRef.current !== null && !rootRef.current.contains(event.target as Node)) close();
    };
    document.addEventListener("mousedown", onPointerDown);
    return () => document.removeEventListener("mousedown", onPointerDown);
  }, [open, close]);

  /**
   * A fixed popup does not move with its trigger, so anything that moves the
   * trigger has to re-place it: scrolling the page, scrolling the dialog it is
   * inside, or resizing the window.
   *
   * `capture: true` on scroll because scroll does not bubble — without it a
   * dialog's own scrollbar would slide the field out from under its list.
   */
  useEffect(() => {
    if (!open) return;

    place();
    window.addEventListener("resize", place);
    window.addEventListener("scroll", place, true);

    return () => {
      window.removeEventListener("resize", place);
      window.removeEventListener("scroll", place, true);
    };
  }, [open, place]);

  function choose(option: SelectOption): void {
    onChange(option.value);
    close();
  }

  function onKeyDown(event: React.KeyboardEvent): void {
    // Escape is handled in capture on the whole control — see the note there.
    if (event.key === "Escape") return;

    if (event.key === "Enter") {
      event.preventDefault();
      const option = filtered[active];
      if (option !== undefined) choose(option);
      return;
    }

    const move = (to: number): void => {
      event.preventDefault();
      if (filtered.length === 0) return;
      const next = Math.max(0, Math.min(filtered.length - 1, to));
      setActive(next);
      const row = listRef.current?.children[next];
      if (row instanceof HTMLElement) row.scrollIntoView({ block: "nearest" });
    };

    if (event.key === "ArrowDown") move(active + 1);
    else if (event.key === "ArrowUp") move(active - 1);
    else if (event.key === "Home") move(0);
    else if (event.key === "End") move(filtered.length - 1);
  }

  return (
    <div
      className={styles.field}
      ref={rootRef}
      /**
       * **Escape closes the list and stops there** — wherever focus is inside
       * this control.
       *
       * It was handled on the search box only, so which of the two it did
       * depended on whether focus had reached the box yet: press it a beat too
       * early and the key went straight past to `Modal`'s own `window`
       * listener, closing the dialog behind. In the booking form that threw
       * away a half-filled appointment because somebody changed their mind
       * about the provider.
       *
       * Handled on the whole control, and only while the list is open, so a
       * second Escape still reaches the dialog and closes it — which is what a
       * second press should do.
       */
      onKeyDownCapture={(event) => {
        if (event.key !== "Escape" || !open) return;
        event.preventDefault();
        event.stopPropagation();
        event.nativeEvent.stopImmediatePropagation();
        close();
        triggerRef.current?.focus();
      }}
    >
      <label className={styles.label} htmlFor={id}>
        {/* One flex child, so the star sits against the word, not a gap away. */}
        <span>
          {label}
          {required === true && <RequiredMark />}
        </span>
      </label>

      <button
        ref={triggerRef}
        type="button"
        id={id}
        role="combobox"
        aria-expanded={open}
        aria-controls={listId}
        aria-haspopup="listbox"
        className={`${styles.trigger} ${invalid === true ? styles.invalid : ""}`}
        disabled={disabled}
        onClick={() => setOpen((was) => !was)}
        onKeyDown={(event) => {
          // Arrow keys open the list from the closed trigger, as a native
          // select does — otherwise the keyboard path starts with a mouse.
          if (!open && (event.key === "ArrowDown" || event.key === "Enter" || event.key === " ")) {
            event.preventDefault();
            setOpen(true);
          }
        }}
        data-testid={testId}
      >
        <span className={selected === undefined ? styles.placeholder : undefined}>
          {selected?.label ?? placeholder}
        </span>
        <span aria-hidden="true" className={styles.chevron}>
          ▾
        </span>
      </button>

      {open && anchor !== null && (
        <div
          className={styles.popup}
          // Measured rather than declared — see `place` above and the note on
          // `.popup` in the stylesheet.
          style={{
            top: anchor.top,
            bottom: anchor.bottom,
            left: anchor.left,
            width: anchor.width,
            maxHeight: anchor.maxHeight,
          }}
        >
          <input
            ref={attachSearch}
            type="text"
            className={styles.search}
            placeholder="Type to search…"
            value={query}
            onChange={(event) => setQuery(event.target.value)}
            onKeyDown={onKeyDown}
            aria-label={`Search ${label}`}
            aria-controls={listId}
            aria-activedescendant={
              filtered[active] === undefined ? undefined : `${listId}-${String(active)}`
            }
            data-testid={`${testId}-search`}
          />

          <ul
            id={listId}
            role="listbox"
            className={styles.list}
            ref={attachList}
            data-testid={`${testId}-list`}
          >
            {filtered.map((option, index) => {
              const isSelected = option.value === value;
              const showGroup =
                option.group !== undefined && option.group !== filtered[index - 1]?.group;

              return (
                <li
                  key={`${option.value}-${String(index)}`}
                  id={`${listId}-${String(index)}`}
                  role="option"
                  aria-selected={isSelected}
                  className={[
                    styles.option,
                    index === active ? styles.active : "",
                    isSelected ? styles.selected : "",
                  ]
                    .filter(Boolean)
                    .join(" ")}
                  // `mousedown` rather than `click`: the outside-click handler
                  // above fires on mousedown, and a click would arrive after
                  // the popup had already closed.
                  onMouseDown={(event) => {
                    event.preventDefault();
                    choose(option);
                  }}
                  onMouseEnter={() => setActive(index)}
                >
                  {showGroup && <span className={styles.group}>{option.group}</span>}
                  <span className={styles.optionLabel}>{option.label}</span>
                  {option.hint !== undefined && <span className={styles.hint}>{option.hint}</span>}
                </li>
              );
            })}

            {filtered.length === 0 && (
              <li className={styles.none} data-testid={`${testId}-none`}>
                Nothing matches “{query}”.
              </li>
            )}
          </ul>
        </div>
      )}

      {note !== undefined && <span className={styles.note}>{note}</span>}
    </div>
  );
}
